import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { Stack, Typography } from "@mui/material";

import { PageContent } from "../../components";
import { useGetUserProfile, usePutUserProfile } from "../../hooks";
import { getAuthUser, getUserPermissions } from "../../redux/auth";

import UserProfilePageContent from "./UserProfilePageContent";
import { UserProfileContext } from "./context";
import {
  buildUpdatePayload,
  getDefaultValues,
  userHasUsersEdit,
} from "./helper";

export const UserProfilePage = ({ currentUser, permissions = [] }) => {
  const { userId } = useParams();
  const targetId = Number(userId);

  const isSelf = currentUser?.id === targetId;
  const canEditOthers = userHasUsersEdit(permissions);
  const canEdit = isSelf || canEditOthers;
  const showAdminSection = canEditOthers;

  const { data, isLoading } = useGetUserProfile(targetId);
  const { mutate, isLoading: isSaving } = usePutUserProfile(targetId);

  const defaults = useMemo(() => getDefaultValues(data), [data]);

  const onSubmit = useCallback(
    (form) => mutate(buildUpdatePayload({ form, targetId, canEditOthers })),
    [canEditOthers, mutate, targetId],
  );

  const ctx = useMemo(
    () => ({ data, canEdit, showAdminSection, isSaving, defaults, onSubmit }),
    [data, canEdit, showAdminSection, isSaving, defaults, onSubmit],
  );

  if (!isLoading && !data) {
    return (
      <PageContent>
        <Stack p={4} gap={2}>
          <Typography variant="h5">User not found</Typography>
          <Typography color="text.secondary">
            You don't have permission to view this profile, or it doesn't exist.
          </Typography>
        </Stack>
      </PageContent>
    );
  }

  return (
    <PageContent disablePadding isLoading={isLoading}>
      <UserProfileContext.Provider value={ctx}>
        <UserProfilePageContent />
      </UserProfileContext.Provider>
    </PageContent>
  );
};

const mapStateToProps = (state) => ({
  currentUser: getAuthUser(state),
  permissions: getUserPermissions(state),
});

export default connect(mapStateToProps)(UserProfilePage);
